import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Hand, Zap, Circle, Activity, Bone, MousePointer } from 'lucide-react';

const services = [
  {
    icon: Hand,
    title: 'Carpal Tunnel Syndrome',
    description: 'Minimally invasive treatment for numbness, tingling, and weakness in the hand caused by median nerve compression.',
    link: '#carpal-tunnel',
  },
  {
    icon: MousePointer,
    title: 'Trigger Finger',
    description: 'Expert treatment for finger locking or catching when bending, restoring smooth movement and function.',
    link: '#trigger-finger',
  },
  {
    icon: Circle,
    title: 'Ganglion Cysts',
    description: 'Safe removal of fluid-filled lumps on the wrist or hand that can cause discomfort and limit mobility.',
    link: '#ganglion-cysts',
  },
  {
    icon: Zap,
    title: 'Cubital Tunnel Syndrome',
    description: 'Treatment for ulnar nerve compression causing numbness in the ring and small fingers.',
    link: '#cubital-tunnel',
  },
  {
    icon: Bone,
    title: 'Hand & Wrist Fractures',
    description: 'Comprehensive fracture care from casting to surgical intervention for optimal healing.',
    link: '#fractures',
  },
  {
    icon: Activity,
    title: "Dupuytren's Contractures",
    description: 'Treatment for abnormal tissue thickening that causes fingers to bend toward the palm.',
    link: '#dupuytrens',
  },
];

export const ServicesSection = () => {
  return (
    <section id="services" className="py-20 lg:py-28 bg-gradient-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Hand className="w-4 h-4" />
            <span className="text-sm font-semibold">Treatment Areas</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Conditions We Treat
          </h2>
          <p className="text-lg text-muted-foreground">
            Specialized care for all conditions affecting your hands, wrists, and elbows. 
            Our expert surgeons provide both surgical and non-surgical treatment options.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <Card 
              key={service.title}
              className="group bg-card border-0 shadow-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-6 lg:p-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <service.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground mb-6 flex-grow">
                  {service.description}
                </p>
                <a 
                  href={service.link}
                  className="inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Don't see your condition listed? We treat many other hand and upper extremity conditions.
          </p>
          <Button variant="outline" size="lg" asChild>
            <a href="#contact">
              Contact Us to Learn More
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
